import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import NewsItem from './NewsItem';

const NewsListContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.border};
  background-color: ${({ theme }) => theme.bgSecondary};
`;

const NewsListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid ${({ theme }) => theme.border};
`;

const NewsListTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.text};
`;

const NewsCount = styled.span`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.textSecondary};
  padding: 0.25rem 0.5rem;
  border: 1px solid ${({ theme }) => theme.border};
`;

const FilterSearchContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.25rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1rem;
    position: sticky;
    top: 0;
    z-index: 100;
    background-color: ${({ theme }) => theme.bg};
    padding-top: 1rem;
    padding-bottom: 1rem;
    margin-top: -1.25rem;
    left: 0;
    right: 0;
    margin-left: calc((100vw - 100%) / -2);
    margin-right: calc((100vw - 100%) / -2);
    padding-left: calc((100vw - 100%) / 2);
    padding-right: calc((100vw - 100%) / 2);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
  
  @media (max-width: 480px) {
    gap: 0.75rem;
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    gap: 0.4rem;
    width: 100%;
  }
  
  @media (max-width: 480px) {
    gap: 0.35rem;
  }
`;

const SearchInputWrapper = styled.div`
  position: relative;
  width: 200px;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchInput = styled.input`
  padding: 0.5rem 1rem;
  padding-right: ${({ $hasValue }) => $hasValue ? '2.5rem' : '1rem'};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 0.25rem;
  font-size: 0.9rem;
  font-weight: 500;
  background-color: ${({ theme }) => theme.bgSecondary};
  color: ${({ theme }) => theme.text};
  min-height: 44px;
  width: 100%;
  box-sizing: border-box;
  transition: all 0.2s ease;
  
  &::placeholder {
    color: ${({ theme }) => theme.textSecondary};
    opacity: 0.7;
  }
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.accent};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.accent}20;
  }
  
  &:hover {
    border-color: ${({ theme }) => theme.accent};
  }
  
  @media (max-width: 768px) {
    padding: 0.45rem 0.85rem;
    padding-right: ${({ $hasValue }) => $hasValue ? '2.25rem' : '0.85rem'};
    font-size: 0.85rem;
    min-height: 40px;
  }
  
  @media (max-width: 480px) {
    padding: 0.4rem 0.75rem;
    padding-right: ${({ $hasValue }) => $hasValue ? '2rem' : '0.75rem'};
    font-size: 0.8rem;
    min-height: 38px;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.textSecondary};
  opacity: 0.6;
  transition: all 0.2s ease;
  border-radius: 0.25rem;
  min-width: 24px;
  min-height: 24px;
  
  &:hover {
    opacity: 1;
    background-color: ${({ theme }) => theme.hover};
    color: ${({ theme }) => theme.text};
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
  }
  
  &:focus {
    outline: none;
    opacity: 1;
    background-color: ${({ theme }) => theme.hover};
  }
  
  @media (max-width: 768px) {
    right: 0.4rem;
    min-width: 22px;
    min-height: 22px;
  }
  
  @media (max-width: 480px) {
    right: 0.35rem;
    min-width: 20px;
    min-height: 20px;
  }
`;

const ClearIcon = styled.span`
  font-size: 1.2rem;
  line-height: 1;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const FilterButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'active' && prop !== '$active',
})`
  padding: 0.5rem 1rem;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 0.25rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${({ $active, theme }) => 
    $active ? theme.accent : 'transparent'};
  color: ${({ $active, theme }) => 
    $active ? theme.bg : theme.text};
  min-height: 44px; /* Touch-friendly minimum size */
  
  &:hover {
    background-color: ${({ $active, theme }) => 
      $active ? theme.accent : theme.hover};
  }
  
  @media (max-width: 768px) {
    padding: 0.45rem 0.85rem;
    font-size: 0.85rem;
    min-height: 40px;
    flex: 1;
  }
  
  @media (max-width: 480px) {
    padding: 0.4rem 0.75rem;
    font-size: 0.8rem;
    min-height: 38px;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem 0;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${({ theme }) => theme.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: 1px solid ${({ theme }) => theme.border};
  background-color: ${({ theme }) => theme.bgSecondary};
`;

const LoadingSpinner = styled.div`
  width: 1.2rem;
  height: 1.2rem;
  border: 2px solid ${({ theme }) => theme.border};
  border-left-color: ${({ theme }) => theme.accent};
  animation: spin 0.8s linear infinite;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem 0;
  font-size: 0.9rem;
  font-weight: 500;
  color: #dc3545;
  background-color: ${({ theme }) => theme.bgSecondary};
  border: 1px solid #dc3545;
  margin: 1rem 0;
`;

const UpdateInfoBox = styled.div`
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${({ theme }) => theme.text};
  background-color: ${({ theme }) => theme.accent}20;
  border: 1px solid ${({ theme }) => theme.accent};
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.accent}30;
  }
  
  @media (max-width: 768px) {
    padding: 0.65rem 0.85rem;
    font-size: 0.85rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.6rem 0.75rem;
    font-size: 0.8rem;
  }
`;

const UpdateInfoText = styled.span`
  flex: 1;
`;

const UpdateButton = styled.button`
  padding: 0.4rem 1rem;
  border: 1px solid ${({ theme }) => theme.accent};
  border-radius: 0.25rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${({ theme }) => theme.accent};
  color: ${({ theme }) => theme.bg};
  white-space: nowrap;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 0.5rem;
  }
`;

const LoadMoreContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
`;

const LoadMoreButton = styled.button`
  padding: 0.75rem 2rem;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 0.25rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  background-color: ${({ $disabled, theme }) => 
    $disabled ? theme.bgSecondary : theme.accent};
  color: ${({ $disabled, theme }) => 
    $disabled ? theme.textSecondary : theme.bg};
  min-height: 44px;
  opacity: ${({ $disabled }) => $disabled ? 0.6 : 1};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 0.65rem 1.75rem;
    font-size: 0.85rem;
    min-height: 40px;
    width: 100%;
    max-width: 300px;
  }
  
  @media (max-width: 480px) {
    padding: 0.6rem 1.5rem;
    font-size: 0.8rem;
    min-height: 38px;
  }
`;

const ButtonSpinner = styled(LoadingSpinner)`
  width: 1rem;
  height: 1rem;
  border-width: 2px;
  flex-shrink: 0;
`;

const EndMessage = styled.div`
  text-align: center;
  padding: 1rem 0;
  font-size: 0.85rem;
  font-weight: 500;
  color: ${({ theme }) => theme.textSecondary};
  font-style: italic;
`;

const NewsList = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updateFailed, setUpdateFailed] = useState(false);
  const [filter, setFilter] = useState('finnish');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    const fetchNews = async (showLoading = false, clearOnError = false) => {
      try {
        if (showLoading) {
          setLoading(true);
        }
        setUpdateFailed(false);
        setError(null);
        
        // Use environment variable if defined, otherwise default to empty string for relative path (uses proxy)
        const apiUrl = process.env.REACT_APP_API_URL || '';
        
        const allNews = [];
        let page = 1;
        let finnishCount = 0;
        let internationalCount = 0;
        const minItemsPerCategory = 100;
        let lastSuccessfulPage = 1;
        
        // Fetch pages until both categories have at least 100 items
        while (finnishCount < minItemsPerCategory || internationalCount < minItemsPerCategory) {
          const response = await fetch(`${apiUrl}/api/news?page=${page}`);
          
          if (!response.ok) {
            // If we get an error (e.g., page doesn't exist), break the loop
            if (response.status === 404 || page > 1) {
              setHasMorePages(false);
              break;
            }
            throw new Error(`API request failed: ${response.status}`);
          }
          
          const data = await response.json();
          
          if (!data || !data.items) {
            // If no items in response, break the loop
            if (page === 1) {
              throw new Error('Invalid data format from API');
            }
            setHasMorePages(false);
            break;
          }
          
          // If we got an empty page, stop fetching
          if (data.items.length === 0) {
            setHasMorePages(false);
            break;
          }
          
          // Add items to the collection
          allNews.push(...data.items);
          lastSuccessfulPage = page;
          
          // Count items by category
          for (let i = 0; i < data.items.length; i++) {
            const item = data.items[i];
            if (item.region === 'finnish') {
              finnishCount++;
            } else if (item.region === 'international') {
              internationalCount++;
            }
          }
          
          // Move to next page
          page++;
          
          // Safety limit to prevent infinite loops (e.g., if API has issues)
          if (page > 50) {
            console.warn('Reached maximum page limit (50). Stopping fetch.');
            setHasMorePages(false);
            break;
          }
        }
        
        setNews(allNews);
        setCurrentPage(lastSuccessfulPage);
        if (showLoading) {
          setLoading(false);
        }

      } catch (error) {
        console.error('Error fetching news:', error);
        setError(error.message);
        
        // Only clear news if this is the initial load and clearOnError is true
        if (clearOnError && showLoading) {
          setNews([]);
          setLoading(false);
        } else {
          // Otherwise, keep existing news and show update failed message
          setUpdateFailed(true);
          if (showLoading) {
            setLoading(false);
          }
        }
      }
    };

    // Fetch immediately on mount
    fetchNews(true, true);

    // Fetch periodically every 15 minutes
    const interval = setInterval(() => {
      fetchNews(false, false);
    }, 15 * 60 * 1000);

    // Handle page visibility changes to resume updates when tab becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Tab became visible, immediately try to fetch news
        fetchNews(false, false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Clear interval and event listener on unmount
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const loadMore = async () => {
    if (loadingMore || !hasMorePages) {
      return;
    }

    try {
      setLoadingMore(true);
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const nextPage = currentPage + 1;
      
      const response = await fetch(`${apiUrl}/api/news?page=${nextPage}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setHasMorePages(false);
          setLoadingMore(false);
          return;
        }
        throw new Error(`API request failed: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data || !data.items || data.items.length === 0) {
        setHasMorePages(false);
        setLoadingMore(false);
        return;
      }
      
      // Append new items to existing news
      setNews(prevNews => [...prevNews, ...data.items]);
      setCurrentPage(nextPage);
      setLoadingMore(false);
      
    } catch (error) {
      console.error('Error loading more news:', error);
      setError(error.message);
      setLoadingMore(false);
    }
  };

  const handleRetryUpdate = async () => {
    if (retrying) return;
    
    setRetrying(true);
    setUpdateFailed(false);
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      
      const allNews = [];
      let page = 1;
      let finnishCount = 0;
      let internationalCount = 0;
      const minItemsPerCategory = 100;
      let lastSuccessfulPage = 1;
      
      // Fetch pages until both categories have at least 100 items
      while (finnishCount < minItemsPerCategory || internationalCount < minItemsPerCategory) {
        const response = await fetch(`${apiUrl}/api/news?page=${page}`);
        
        if (!response.ok) {
          if (response.status === 404 || page > 1) {
            setHasMorePages(false);
            break;
          }
          throw new Error(`API request failed: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (!data || !data.items) {
          if (page === 1) {
            throw new Error('Invalid data format from API');
          }
          setHasMorePages(false);
          break;
        }
        
        if (data.items.length === 0) {
          setHasMorePages(false);
          break;
        }
        
        allNews.push(...data.items);
        lastSuccessfulPage = page;
        
        // Count items by category
        for (let i = 0; i < data.items.length; i++) {
          const item = data.items[i];
          if (item.region === 'finnish') {
            finnishCount++;
          } else if (item.region === 'international') {
            internationalCount++;
          }
        }
        
        page++;
        
        if (page > 50) {
          console.warn('Reached maximum page limit (50). Stopping fetch.');
          setHasMorePages(false);
          break;
        }
      }
      
      setNews(allNews);
      setCurrentPage(lastSuccessfulPage);
      setUpdateFailed(false);
      setError(null);
    } catch (error) {
      console.error('Error retrying update:', error);
      setUpdateFailed(true);
      setError(error.message);
    } finally {
      setRetrying(false);
    }
  };

  if (loading) {
    return (
      <LoadingMessage>
        <LoadingSpinner />
        Ladataan uutisia...
      </LoadingMessage>
    );
  }

  // Only show error message if we have no news at all (initial load failed)
  if (error && news.length === 0) {
    return <ErrorMessage>{error}</ErrorMessage>;
  }

  // Filter news based on selected region
  const filteredNews = news.filter(item => {
    if (filter === 'all') {
      return true;
    } else if (filter === 'finnish') {
      return item.region === 'finnish';
    } else if (filter === 'international') {
      return item.region === 'international';
    }
    return true;
  });

  // Filter news based on search query (searches in title/headline)
  const searchedNews = filteredNews.filter(item => {
    if (!searchQuery.trim()) {
      return true;
    }
    return item.title.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Handle filter change and clear search if filter is different
  const handleFilterChange = (newFilter) => {
    if (newFilter !== filter) {
      setSearchQuery('');
    }
    setFilter(newFilter);
  };

  return (
    <>
      {updateFailed && (
        <UpdateInfoBox onClick={handleRetryUpdate}>
          <UpdateInfoText>
            Uutisten päivitys epäonnistui. Klikkaa päivittääksesi uutiset.
          </UpdateInfoText>
          <UpdateButton disabled={retrying}>
            {retrying ? 'Päivitetään...' : 'Päivitä'}
          </UpdateButton>
        </UpdateInfoBox>
      )}
      <NewsListHeader>
        <NewsListTitle>Uusimmat uutiset</NewsListTitle>
        {searchedNews.length > 0 && <NewsCount>{searchedNews.length} uutista</NewsCount>}
      </NewsListHeader>
      <FilterSearchContainer>
        <FilterContainer>
          <FilterButton 
            $active={filter === 'finnish'} 
            onClick={() => handleFilterChange('finnish')}
          >
            Suomesta
          </FilterButton>
          <FilterButton 
            $active={filter === 'international'} 
            onClick={() => handleFilterChange('international')}
          >
            Maailmalta
          </FilterButton>
          <FilterButton 
            $active={filter === 'all'} 
            onClick={() => handleFilterChange('all')}
          >
            Kaikki
          </FilterButton>
        </FilterContainer>
        <SearchInputWrapper>
          <SearchInput
            type="text"
            placeholder="Etsi uutisista..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            $hasValue={searchQuery.length > 0}
          />
          {searchQuery.length > 0 && (
            <ClearButton
              onClick={() => setSearchQuery('')}
              aria-label="Tyhjennä haku"
              title="Tyhjennä haku"
            >
              <ClearIcon>×</ClearIcon>
            </ClearButton>
          )}
        </SearchInputWrapper>
      </FilterSearchContainer>
      <NewsListContainer>
        {searchedNews.map(item => (
          <NewsItem key={item.id} news={item} />
        ))}
      </NewsListContainer>
      <LoadMoreContainer>
        {hasMorePages ? (
          <LoadMoreButton
            onClick={loadMore}
            disabled={loadingMore}
            $disabled={loadingMore}
          >
            {loadingMore ? (
              <>
                <ButtonSpinner />
                Ladataan...
              </>
            ) : (
              'Lataa lisää'
            )}
          </LoadMoreButton>
        ) : (
          <EndMessage>
            Kaikki uutiset on ladattu.
          </EndMessage>
        )}
      </LoadMoreContainer>
    </>
  );
};

export default NewsList;
